import { apiFetch } from "./apiFetch";
import { saveBlob } from "./downloadFile";

/**
 * Screenshots, log files and execution recordings are served by the backend
 * behind the same `Authorization: Bearer <token>` check as every other API
 * call. An element `src`/`href` cannot send a header, so the bytes are fetched
 * here — with the credential attached by `apiFetch` — and handed to the browser
 * as a blob instead.
 */

const HTTP_MESSAGES = {
  401: "Your session has expired. Sign in again to view this file.",
  403: "You are not authorized to view this file.",
  404: "This file is no longer available on the server.",
};

const messageForStatus = (status) => {
  if (HTTP_MESSAGES[status]) return HTTP_MESSAGES[status];
  if (status >= 500) return "The server had a problem loading this file. Please try again.";
  return "Failed to load the file.";
};

/** Error shapes the servlet returns instead of a file, all at HTTP 200. */
const isServletError = (parsed) =>
  parsed?.code === "TOKEN_INVALID" || parsed?.status === "error" || parsed?.message === "fail";

/**
 * The file servlets report failures in the body, not the status line — an
 * unauthenticated request comes back as 200 with `{"message":"fail"}`, and it
 * does not always carry a JSON content type. Left undetected that becomes a
 * blob URL of an error string handed to an <img>, i.e. a silently blank image.
 */
const assertNotErrorPayload = async (blob) => {
  const looksJson = blob.type.includes("json");

  const worthSniffing = blob.size <= 4096 && !/^(image|video|audio)\//.test(blob.type);
  if (!looksJson && !worthSniffing) return;

  let parsed;
  try {
    parsed = JSON.parse(await blob.text());
  } catch {
    if (looksJson) throw new Error("The server did not return a file.");
    return;
  }

  if (!looksJson && !isServletError(parsed)) return;

  const message =
    parsed?.message === "fail"
      ? "You are not authorized to view this file."
      : parsed?.message ?? parsed?.error ?? parsed?.status ?? "The server did not return a file.";
  throw new Error(message);
};

export const fetchAuthedBlob = async (url) => {
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(messageForStatus(response.status));
  }

  const blob = await response.blob();
  await assertNotErrorPayload(blob);
  return blob;
};

/**
 * Downloads a protected file and returns a blob URL for it. The caller owns the
 * URL and must revoke it once the element using it is gone.
 */
export const fetchAuthedObjectUrl = async (url) => URL.createObjectURL(await fetchAuthedBlob(url));

/**
 * Opens a protected file in a new tab.
 *
 * Call this straight from the click handler: the tab is opened synchronously so
 * the browser still credits it to the user's gesture, then pointed at the blob
 * once the download finishes. If the popup was blocked the file is saved
 * instead, so the click is never a silent no-op.
 */
export const openAuthedResource = async (url, { filename = "download" } = {}) => {
  const tab = window.open("", "_blank");

  try {
    const blob = await fetchAuthedBlob(url);

    if (!tab || tab.closed) {
      saveBlob(blob, filename);
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    tab.location.href = objectUrl;

    // Revoking before the new tab has loaded cancels its navigation, and we get
    // no load event from a cross-tab document — so give it a generous window.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (error) {
    tab?.close();
    throw error;
  }
};

export default openAuthedResource;
